import { useI18n } from '@ecom/i18n';
import { useEffect, useRef, useState, type ReactElement } from 'react';

import { copyText } from '../utils/copy-text';

import { Button } from './Button';

/** How long the password stays on screen if nobody acts. */
export const TEMPORARY_PASSWORD_DISPLAY_SECONDS = 60;

export interface TemporaryPasswordPanelProps {
  /** The account the password belongs to (an address): shown so the person handing it over does not mix two accounts up. */
  readonly account: string;
  /** The provisional password. The caller keeps it in component state ONLY and drops it when `onDone` / `onExpire` fire. */
  readonly password: string;
  /** ISO date of the moment the password stops working. */
  readonly expiresAt: string;
  /** The person confirms they noted it: the caller must forget the password. */
  readonly onDone: () => void;
  /** The display window ended without an action: the caller must forget the password. */
  readonly onExpire?: () => void;
  /** Overrides the 60 s window (tests). */
  readonly displaySeconds?: number;
}

/**
 * A provisional password, shown ONCE.
 *
 * ---------------------------------------------------------------------
 * WHY IT LOOKS AND BEHAVES THE WAY IT DOES
 * ---------------------------------------------------------------------
 * The password is a secret that travels through a person (a superadmin, a shop owner) on its way to another. Every convenience here
 * is weighed against that:
 *
 *   * masked until asked: a screen share, a photo over the shoulder or a recording sees dots, not the secret;
 *   * grouped in fours for reading aloud, but copied WITHOUT the grouping, from a value that is never put in the page as an editable
 *     field - the group spacing is layout, not characters;
 *   * a countdown: after 60 seconds, or when the person says they noted it, the password is gone from this component and from the
 *     caller's state (`onDone` / `onExpire`). It is never recoverable: a lost one is replaced, not re-displayed;
 *   * it says plainly that the clipboard may keep a copy, and that no email was sent.
 *
 * The component never writes the password to storage, a URL, a log or an attribute other than the clipboard on request.
 */
export function TemporaryPasswordPanel({
  account,
  password,
  expiresAt,
  onDone,
  onExpire,
  displaySeconds = TEMPORARY_PASSWORD_DISPLAY_SECONDS,
}: TemporaryPasswordPanelProps): ReactElement {
  const { t, locale } = useI18n();
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState<'idle' | 'ok' | 'failed'>('idle');
  const [gone, setGone] = useState(false);
  const expire = useRef(onExpire);
  expire.current = onExpire;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setGone(true);
      setRevealed(false);
      expire.current?.();
    }, displaySeconds * 1_000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [displaySeconds]);

  const until = new Date(expiresAt).toLocaleString(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  if (gone) {
    return (
      <section className="ecom-temp-password" aria-labelledby="ecom-temp-password-title">
        <h3 id="ecom-temp-password-title" className="ecom-temp-password__title">
          {t('common.tempPassword.title')}
        </h3>
        <p role="status" className="ecom-temp-password__gone">
          {t('common.tempPassword.gone')}
        </p>
        <div className="ecom-temp-password__actions">
          <Button type="button" onClick={onDone}>
            {t('common.close')}
          </Button>
        </div>
      </section>
    );
  }

  const groups = password.match(/.{1,4}/gu) ?? [];

  return (
    <section className="ecom-temp-password" aria-labelledby="ecom-temp-password-title">
      <h3 id="ecom-temp-password-title" className="ecom-temp-password__title">
        {t('common.tempPassword.title')}
      </h3>
      <p className="ecom-temp-password__warning">{t('common.tempPassword.once')}</p>

      <dl className="ecom-temp-password__facts">
        <div>
          <dt>{t('common.tempPassword.account')}</dt>
          <dd dir="ltr">{account}</dd>
        </div>
        <div>
          <dt>{t('common.tempPassword.label')}</dt>
          <dd>
            <output
              className="ecom-temp-password__value"
              dir="ltr"
              data-revealed={revealed ? 'true' : 'false'}
              aria-label={t('common.tempPassword.label')}
            >
              {revealed
                ? groups.map((group, index) => (
                    <span key={index} className="ecom-temp-password__group">
                      {group}
                    </span>
                  ))
                : '••••••••••••••••••••'}
            </output>
          </dd>
        </div>
      </dl>

      <div className="ecom-temp-password__actions">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-pressed={revealed}
          onClick={() => {
            setRevealed((current) => !current);
          }}
        >
          {revealed ? t('common.tempPassword.hide') : t('common.tempPassword.show')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            void copyText(password).then((ok) => {
              setCopied(ok ? 'ok' : 'failed');
            });
          }}
        >
          {t('common.tempPassword.copy')}
        </Button>
      </div>

      <p role="status" className="ecom-temp-password__copy-state">
        {copied === 'ok' ? t('common.tempPassword.copied') : null}
        {copied === 'failed' ? t('common.tempPassword.copyFailed') : null}
      </p>

      <ul className="ecom-temp-password__notes">
        <li>{t('common.tempPassword.expires', { date: until })}</li>
        <li>{t('common.tempPassword.mustChange')}</li>
        <li>{t('common.tempPassword.clipboard')}</li>
      </ul>

      <div className="ecom-temp-password__actions">
        <Button type="button" onClick={onDone}>
          {t('common.tempPassword.done')}
        </Button>
      </div>
    </section>
  );
}
